/**
 * TaskLinkPicker — portals into layout #taskModal / #task_modal.
 * Mirrors the former Hotwire emails/task_links/search markup and filters.
 */

import { h } from 'preact'
import { createPortal } from 'preact/compat'
import { useEffect, useRef, useState } from 'preact/hooks'
import { buildBuildingComplexesPath } from '@tshmanager/email-shared'
import { apiGet, emailApiBase } from '../../modules/email/store/api.js'
import {
  openTaskModalHost,
  teardownTaskModalHost,
  getTaskModalEl,
} from './taskModalHost.js'
import { unmountTaskModalShell } from '../../modules/task_modal_shell.js'

const SEARCH_DEBOUNCE_MS = 300
const KANBAN_STATUSES = ['open', 'inprogress', 'reject', 'closed', 'scheduled', 'not_fulfilled']

export function TaskLinkPicker({ store, i18n }) {
  const open    = store.taskLinkPickerOpen.value
  const emailId = store.taskLinkEmailId.value

  const hostRef     = useRef(null)
  const inputRef    = useRef(null)
  const debounceRef = useRef(null)
  const [frameEl, setFrameEl] = useState(null)

  const [query, setQuery]         = useState('')
  const [bcId, setBcId]           = useState('')
  const [status, setStatus]       = useState('')
  const [responsibleId, setResponsibleId] = useState('')
  const [buildingComplexes, setBuildingComplexes] = useState([])
  const [tasks, setTasks]         = useState([])
  const [loading, setLoading]     = useState(false)
  const [linkingId, setLinkingId] = useState(null)
  const [error, setError]         = useState('')

  const people = store.peopleWithUser.value || []

  const t = (key, fallback) => {
    const msg = i18n?.t(key, { defaultValue: fallback })
    return !msg || /^\[missing /.test(msg) ? fallback : msg
  }

  useEffect(() => {
    const modal = getTaskModalEl()
    if (!modal) return undefined

    const onHidden = (event) => {
      if (event.target !== modal) return
      if (!hostRef.current) return
      teardownTaskModalHost(hostRef.current)
      hostRef.current = null
      setFrameEl(null)
      store.closeTaskLinkPicker()
    }
    modal.addEventListener('hidden.bs.modal', onHidden)
    return () => modal.removeEventListener('hidden.bs.modal', onHidden)
  }, [])

  useEffect(() => {
    if (!open || !emailId) {
      if (hostRef.current) {
        const host = hostRef.current
        hostRef.current = null
        setFrameEl(null)
        if (host.shell) unmountTaskModalShell(host.shell)
        if (!store.taskCreateOpen.value) host.bs?.hide()
      }
      return
    }

    // Same flex shell as Hotwire email task picker inside #taskModal.
    const host = openTaskModalHost({ flexShell: true, extraClass: null })
    if (!host) return
    hostRef.current = host
    setFrameEl(host.root)

    setQuery('')
    setBcId('')
    setStatus('')
    setResponsibleId('')
    setError('')
    setLinkingId(null)
    requestAnimationFrame(() => inputRef.current?.focus())

    let cancelled = false
    apiGet(buildBuildingComplexesPath(emailApiBase()))
      .then(data => {
        if (cancelled) return
        const list = data?.building_complexes || (Array.isArray(data) ? data : [])
        setBuildingComplexes(list)
      })
      .catch(() => {})

    return () => { cancelled = true }
  }, [open, emailId])

  useEffect(() => {
    if (!open || !emailId) return undefined

    clearTimeout(debounceRef.current)
    let cancelled = false
    setLoading(true)

    const filters = {}
    if (query) filters.q = query
    if (bcId) filters.building_complex_id = bcId
    if (status) filters.status = status
    if (responsibleId) filters.responsible_id = responsibleId

    debounceRef.current = setTimeout(async () => {
      try {
        const data = await store.searchLinkableTasks(emailId, filters)
        if (!cancelled) setTasks(data?.tasks || [])
      } catch (err) {
        if (!cancelled) setError(t('emails.no_tasks_found', 'Nem találhatók feladatok.'))
        console.error('[TaskLinkPicker] search failed', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, query ? SEARCH_DEBOUNCE_MS : 0)

    return () => { cancelled = true; clearTimeout(debounceRef.current) }
  }, [open, emailId, query, bcId, status, responsibleId])

  async function handleLink(taskId) {
    if (linkingId) return
    setLinkingId(taskId)
    setError('')
    try {
      await store.linkTaskToEmail(emailId, taskId)
      hostRef.current?.bs?.hide()
      store.closeTaskLinkPicker()
    } catch (err) {
      setLinkingId(null)
      setError(err?.message || t('emails.link_to_task', 'A csatolás nem sikerült.'))
      console.error('[TaskLinkPicker] link failed', err)
    }
  }

  if (!open || !frameEl) return null

  const content = (
    <div class="email-task-picker-modal d-flex flex-column flex-grow-1 min-h-0 w-100" data-turbo="false">
      <div class="modal-header border-bottom flex-shrink-0">
        <h5 class="modal-title d-flex align-items-center gap-2">
          <i class="bi bi-link-45deg" />
          {t('emails.link_to_task', 'Csatolás feladathoz')}
        </h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label={t('helpers.close', 'Bezárás')} />
      </div>

      <div class="email-task-picker-filters flex-shrink-0 border-bottom bg-body-tertiary">
        <div class="px-3 pt-3 pb-2">
          <div class="input-group input-group-sm mb-2">
            <span class="input-group-text"><i class="bi bi-search" /></span>
            <input
              ref={inputRef}
              type="text"
              class="form-control"
              placeholder={t('emails.search_tasks_placeholder', 'Feladat keresése…')}
              autocomplete="off"
              value={query}
              onInput={e => setQuery(e.target.value)}
            />
          </div>

          <div class="d-flex gap-2 flex-wrap">
            <select
              class="form-select form-select-sm"
              style="min-width:150px; max-width:220px"
              value={bcId}
              onChange={e => setBcId(e.target.value)}
            >
              <option value="">{t('emails.all_buildings', 'Összes társasház')}</option>
              {buildingComplexes.map(bc => (
                <option key={bc.id} value={bc.id}>{bc.name}</option>
              ))}
            </select>

            <select
              class="form-select form-select-sm"
              style="min-width:130px; max-width:180px"
              value={status}
              onChange={e => setStatus(e.target.value)}
            >
              <option value="">{t('tasks.calendar.all_statuses', 'Összes státusz')}</option>
              {KANBAN_STATUSES.map(s => (
                <option key={s} value={s}>{t(`tasks.status.${s}`, s)}</option>
              ))}
            </select>

            <select
              class="form-select form-select-sm"
              style="min-width:130px; max-width:200px"
              value={responsibleId}
              onChange={e => setResponsibleId(e.target.value)}
            >
              <option value="">{t('emails.all_responsible', 'Összes felelős')}</option>
              {people.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div class="modal-body p-0 email-task-picker-body flex-grow-1">
        {error && <div class="alert alert-danger m-3 py-2 small">{error}</div>}

        {loading && (
          <div class="text-center py-5">
            <span class="spinner-border spinner-border-sm" role="status" />
          </div>
        )}

        {!loading && tasks.length === 0 && (
          <div class="text-center text-muted py-5">
            <i class="bi bi-inbox display-6 d-block mb-2" />
            {t('emails.no_tasks_found', 'Nem találhatók feladatok.')}
          </div>
        )}

        {!loading && tasks.length > 0 && (
          <ul class="list-group list-group-flush">
            {tasks.map(task => (
              <li key={task.id} class="list-group-item list-group-item-action py-2 px-3">
                <div class="d-flex align-items-start gap-3">
                  <div class="flex-grow-1 min-w-0">
                    <div class="fw-semibold text-truncate">{task.name}</div>
                    <div class="small text-muted d-flex flex-wrap gap-2 mt-1">
                      {(task.building_complex_names || []).slice(0, 2).map(name => (
                        <span key={name}><i class="bi bi-building me-1" />{name}</span>
                      ))}
                      {task.responsible_name && (
                        <span><i class="bi bi-person me-1" />{task.responsible_name}</span>
                      )}
                      {task.status && (
                        <span class="badge bg-secondary-subtle text-secondary fw-normal">
                          {t(`tasks.status.${task.status}`, task.status)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div class="flex-shrink-0 align-self-center">
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      disabled={linkingId != null}
                      onClick={() => handleLink(task.id)}
                    >
                      {linkingId === task.id
                        ? <span class="spinner-border spinner-border-sm me-1" role="status" />
                        : <i class="bi bi-link-45deg me-1" />
                      }
                      {t('emails.link', 'Csatolás')}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div class="modal-footer py-2 flex-shrink-0">
        <span class="text-muted small me-auto text-truncate">
          {t('emails.task_picker_hint', '')}
        </span>
        <button type="button" class="btn btn-sm btn-secondary flex-shrink-0" data-bs-dismiss="modal">
          {t('helpers.close', 'Bezárás')}
        </button>
      </div>
    </div>
  )

  return createPortal(content, frameEl)
}
